import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { callFunction } from '../services/api';
import { trackGoogleAdsConversion, CONVERSION_LABELS } from '../utils/googleAdsTracking';
import styles from './ContactPage.module.css';

const ContactPage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    website: '' // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (reason === 'state-support') {
      setFormData((prev) => ({
        ...prev,
        subject: 'state-support'
      }));
    }
    
    // Check for Team License navigation state
    if (location.state?.teamLicense) {
      setFormData((prev) => ({
        ...prev,
        subject: 'team-license'
      }));
    }
  }, [location.state]);

  // Track Google Ads conversion when success message appears after form submission
  useEffect(() => {
    const handleSubmitClick = (e) => {
      if (e.target.closest(`.${styles.submitBtn}`)) {
        const setTimer = setInterval(() => {
          const successMessage = document.querySelector(`.${styles.successMessage}`);
          if (successMessage && successMessage.innerText.includes('Thank you')) {
            if (CONVERSION_LABELS.CONTACT_FORM) {
              trackGoogleAdsConversion(CONVERSION_LABELS.CONTACT_FORM);
            }
            clearInterval(setTimer);
          }
        }, 1000);
        
        // Cleanup: clear interval after 30 seconds to prevent infinite checking
        setTimeout(() => {
          clearInterval(setTimer);
        }, 30000);
      }
    };

    document.addEventListener('click', handleSubmitClick);
    
    return () => {
      document.removeEventListener('click', handleSubmitClick);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Rate limiting: Check if user submitted within last 60 seconds
      const now = Date.now();
      if (lastSubmissionTime && (now - lastSubmissionTime) < 60000) {
        setSubmitStatus('rate_limit');
        return;
      }

      // Validate required fields
      if (!formData.name || !formData.email || !formData.subject || !formData.message) {
        setSubmitStatus('error');
        return;
      }

      // Enhanced email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setSubmitStatus('error');
        return;
      }

      // Honeypot protection: If website field is filled, it's likely a bot
      if (formData.website.trim()) {
        setSubmitStatus('spam_detected');
        return;
      }

      // Spam protection: Check for suspicious patterns
      const suspiciousPatterns = [
        /(http|https|www\.)/i, // URLs
        /(viagra|casino|poker|lottery|winner|congratulations)/i, // Common spam words
        /(click here|buy now|free money|guaranteed)/i, // Marketing phrases
        /(bitcoin|crypto|investment|trading)/i, // Crypto spam
        /(loan|debt|credit|mortgage)/i // Financial spam
      ];

      const messageText = formData.message.toLowerCase();
      const nameText = formData.name.toLowerCase();
      const subjectText = formData.subject.toLowerCase();
      
      for (const pattern of suspiciousPatterns) {
        if (pattern.test(messageText) || pattern.test(nameText) || pattern.test(subjectText)) {
          setSubmitStatus('spam_detected');
          return;
        }
      }

      // Length limits to prevent abuse
      if (formData.message.length > 2000) {
        setSubmitStatus('error');
        return;
      }

      if (formData.name.length > 100 || formData.email.length > 100 || formData.subject.length > 200) {
        setSubmitStatus('error');
        return;
      }

      const subjectLabels = {
        general: 'General Inquiry',
        support: 'Technical Support',
        sales: 'Sales Question',
        partnership: 'Partnership',
        'state-support': 'State Support',
        'team-license': 'Team License',
        other: 'Other'
      };

      const readableSubject = subjectLabels[formData.subject] || formData.subject || 'Vates Domus AI Contact Form Submission';
      
      await callFunction('contactMessage', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: readableSubject,
        message: [
          formData.company ? `Company: ${formData.company.trim()}` : '',
          '',
          formData.message.trim(),
        ].filter(Boolean).join('\n'),
      });
      
      // Update last submission time for rate limiting
      setLastSubmissionTime(now);
      
      setSubmitStatus('success');
      
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: '',
        website: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div className={styles.contactPage}>
        <div className={styles.contactContainer}>
          {/* Header */}
          <div className={styles.contactHeader}>
            <h1>Get in Touch</h1>
            <p>Have questions about Vates Domus AI? We'd love to hear from you.</p>
          </div>

          <div className={styles.contactContent}>
            {/* Contact Form */}
            <div className={styles.contactFormSection}>
              <h2>Send us a Message</h2>
              <form onSubmit={handleSubmit} className={styles.contactForm}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="company">Company (Optional)</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Enter your company name"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="subject">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="sales">Sales Question</option>
                      <option value="partnership">Partnership</option>
                      <option value="team-license">Team License</option>
                      <option value="state-support">State Support</option>
                      <option value="other">Other</option>
                    </select>
            {formData.subject === 'state-support' && (
              <p className="form-help" style={{ fontStyle: 'italic' }}>
                Please tell us which state you need so we can reach out when Vates Domus AI becomes available there.
              </p>
            )}
                  </div>
                </div>

                {/* Honeypot field - hidden from users */}
                <div style={{ display: 'none' }}>
                  <label htmlFor="website">Website (Leave blank)</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="6"
                    placeholder="Tell us how we can help you..."
                  />
                </div>

                {submitStatus === 'success' && (
                  <div className={styles.successMessage}>
                    ✅ Thank you! Your message has been sent successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className={styles.errorMessage}>
                    ❌ Sorry, there was an error sending your message. Please try again.
                  </div>
                )}

                {submitStatus === 'rate_limit' && (
                  <div className={styles.errorMessage}>
                    ⏰ Please wait at least 60 seconds before submitting another message.
                  </div>
                )}

                {submitStatus === 'spam_detected' && (
                  <div className={styles.errorMessage}>
                    🚫 Your message appears to contain spam content. Please review and try again.
                  </div>
                )}

                <button 
                  type="submit" 
                  className={styles.submitBtn}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className={styles.contactInfoSection}>
              <h2>Other Ways to Reach Us</h2>
              <div className={styles.contactMethods}>
                <div className={styles.contactMethod}>
                  <div className={styles.methodIcon}>📧</div>
                  <div className={styles.methodContent}>
                    <h3>Email</h3>
                    <p>For general inquiries and support</p>
                    <a href="mailto:info@vatesdomus.com">info@vatesdomus.com</a>
                  </div>
                </div>

                <div className={styles.contactMethod}>
                  <div className={styles.methodIcon}>💼</div>
                  <div className={styles.methodContent}>
                    <h3>LinkedIn</h3>
                    <p>Connect with us professionally</p>
                    <a 
                      href="https://www.linkedin.com/company/vates-domus-ai" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Vates Domus AI
                    </a>
                  </div>
                </div>

                <div className={styles.contactMethod}>
                  <div className={styles.methodIcon}>📘</div>
                  <div className={styles.methodContent}>
                    <h3>Facebook</h3>
                    <p>Follow us for news and updates</p>
                    <a
                      href="https://www.facebook.com/profile.php?id=61580357730025"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Vates Domus AI
                    </a>
                  </div>
                </div>

                <div className={styles.contactMethod}>
                  <div className={styles.methodIcon}>📅</div>
                  <div className={styles.methodContent}>
                    <h3>Schedule a Meeting With Us</h3>
                    <p>Book a time to discuss your needs</p>
                    <a 
                      href="https://calendly.com/shep-vatesdomus/45-minute-meeting-clone" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Schedule a Meeting
                    </a>
                  </div>
                </div>

                <div className={styles.contactMethod}>
                  <div className={styles.methodIcon}>⚡</div>
                  <div className={styles.methodContent}>
                    <h3>Response Time</h3>
                    <p>We typically respond within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ContactPage;
